import React, { useState, useEffect } from "react";
import { useRoute, Link, useLocation } from "wouter";
import { trpc } from "@/lib/trpc";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  ArrowLeft, Building2, Users, Scissors, Calendar, Settings,
  Phone, Mail, MapPin, Clock, Loader2, Star, DollarSign,
  CheckCircle2, XCircle, AlertCircle, LogIn, Bot, Link2, Link2Off, RefreshCw,
  Zap, MessageSquare, Activity, ExternalLink, Wifi, WifiOff, Save, TestTube2, QrCode,
  Pause, Play, Share2, Copy, Download
} from "lucide-react";
import { toast } from "sonner";
import { QRCodeCanvas } from "qrcode.react";
import { format, isValid } from "date-fns";
import { ptBR } from "date-fns/locale";

// Formata data com segurança — retorna fallback se o valor for inválido
function safeFormat(value: unknown, fmt: string, fallback = "—"): string {
  if (value == null) return fallback;
  const d = typeof value === "number" ? new Date(value) : new Date(String(value));
  return isValid(d) ? format(d, fmt, { locale: ptBR }) : fallback;
}
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const STATUS_BADGE: Record<string, React.ReactNode> = {
  trial: <Badge className="bg-blue-100 text-blue-700 border-blue-200">Trial</Badge>,
  active: <Badge className="bg-green-100 text-green-700 border-green-200">Ativo</Badge>,
  suspended: <Badge className="bg-red-100 text-red-700 border-red-200">Suspenso</Badge>,
  cancelled: <Badge className="bg-gray-100 text-gray-600 border-gray-200">Cancelado</Badge>,
};

const APPT_STATUS_BADGE: Record<string, React.ReactNode> = {
  pending: <Badge className="bg-yellow-100 text-yellow-700 border-yellow-200">Pendente</Badge>,
  confirmed: <Badge className="bg-blue-100 text-blue-700 border-blue-200">Confirmado</Badge>,
  completed: <Badge className="bg-green-100 text-green-700 border-green-200">Concluído</Badge>,
  cancelled: <Badge className="bg-red-100 text-red-700 border-red-200">Cancelado</Badge>,
  no_show: <Badge className="bg-gray-100 text-gray-600 border-gray-200">Não Compareceu</Badge>,
};

// ─── NativeAgentTab ──────────────────────────────────────────────────────────

function NativeAgentTab({ salonId }: { salonId: number }) {
  const utils = trpc.useUtils();
  const { data: agent, isLoading } = trpc.superAdmin.getAiAgent.useQuery({ salonId });
  const { data: detail } = trpc.superAdmin.getSalonDetail.useQuery({ salonId });
  const { data: connLogs } = trpc.superAdmin.getConnectionLogs.useQuery({ salonId });
  const salonDetail = (detail as any)?.salon ?? null;

  // Wizard state
  const [wizardStep, setWizardStep] = useState<1 | 2 | 3 | 4>(1);
  const [wizardUrl, setWizardUrl] = useState("");
  const [wizardKey, setWizardKey] = useState("");
  const [wizardInstance, setWizardInstance] = useState("");
  const [wizardQrCode, setWizardQrCode] = useState<string | null>(null);
  const [wizardQrText, setWizardQrText] = useState<string | null>(null);
  const [wizardPairingCode, setWizardPairingCode] = useState<string | null>(null);
  const [wizardConnectMode, setWizardConnectMode] = useState<"qr" | "pairing">("qr");
  const [pairingCountdown, setPairingCountdown] = useState(60);
  const [wizardCopied, setWizardCopied] = useState(false);
  const [wizardConnected, setWizardConnected] = useState(false);
  const [pollingActive, setPollingActive] = useState(false);
  const [qrExpireCountdown, setQrExpireCountdown] = useState(60);

  // Legacy manual config state (for already-configured salons)
  const [evoUrl, setEvoUrl] = useState("");
  const [evoInstance, setEvoInstance] = useState("");
  const [evoKey, setEvoKey] = useState("");
  const [evoStatus, setEvoStatus] = useState<"idle" | "connected" | "disconnected">("idle");
  const [evoQrCode, setEvoQrCode] = useState<string | null>(null);
  const [evoQrText, setEvoQrText] = useState<string | null>(null);
  const [evoPairingCode, setEvoPairingCode] = useState<string | null>(null);
  const [evoConnectMode, setEvoConnectMode] = useState<"qr" | "pairing">("qr");
  const [evoPairingCountdown, setEvoPairingCountdown] = useState(60);
  const [evoCopied, setEvoCopied] = useState(false);
  const [showManual, setShowManual] = useState(false);
  const [evoPollingActive, setEvoPollingActive] = useState(false);
  const [evoQrCountdown, setEvoQrCountdown] = useState(60);

  // Polling: verificar conexão a cada 5s após gerar QR Code de reconexão
  useEffect(() => {
    if (!evoPollingActive) return;
    const interval = setInterval(async () => {
      try {
        const res = await fetch(`/api/trpc/superAdmin.getEvolutionInstanceStatus?input=${encodeURIComponent(JSON.stringify({ json: { salonId } }))}`, {
          credentials: "include",
        });
        const json = await res.json() as any;
        const state = json?.result?.data?.json?.state ?? json?.result?.data?.state;
        const webhookConfigured = json?.result?.data?.json?.webhookConfigured ?? json?.result?.data?.webhookConfigured;
        if (state === "open") {
          setEvoPollingActive(false);
          setEvoStatus("connected");
          setEvoQrCode(null);
          setEvoQrText(null);
          setEvoPairingCode(null);
          if (webhookConfigured === false) {
            toast.error("WhatsApp reconectado, mas o webhook não pôde ser ativado automaticamente.");
          } else {
            toast.success("✅ WhatsApp reconectado e webhook ativado!");
          }
          notifyConnectionMutation.mutate({ salonId, method: "qr" });
        }
      } catch { /* ignore */ }
    }, 5000);
    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [evoPollingActive, salonId]);

  const isAlreadyConfigured = !!(salonDetail?.evolutionApiUrl && salonDetail?.evolutionInstance);

  // Pairing Code reconexão: countdown a cada 60s
  useEffect(() => {
    if (!evoPairingCode) return;
    setEvoPairingCountdown(60);
    const tick = setInterval(() => {
      setEvoPairingCountdown((c) => (c <= 1 ? 60 : c - 1));
    }, 1000);
    return () => clearInterval(tick);
  }, [evoPairingCode]);

  // Populate fields when detail loads
  useEffect(() => {
    if (salonDetail) {
      setEvoUrl(salonDetail.evolutionApiUrl ?? "");
      setEvoInstance(salonDetail.evolutionInstance ?? "");
      // A chave nunca é enviada de volta ao navegador. Um campo vazio mantém
      // a credencial válida salva no servidor quando a configuração é editada.
      setEvoKey("");
    }
  }, [salonDetail]);

  // Pairing Code: countdown + auto-refresh a cada 60s
  useEffect(() => {
    if (wizardStep !== 3 || !wizardPairingCode) return;
    setPairingCountdown(60);
    const tick = setInterval(() => {
      setPairingCountdown((c) => {
        if (c <= 1) {
          createInstanceMutation.mutate({
            salonId,
            // A chave já foi salva no passo 1. Nunca reenviar um valor que
            // tenha ficado em memória, pois ele pode estar desatualizado.
            instanceName: wizardInstance || evoInstance || undefined,
          });
          return 60;
        }
        return c - 1;
      });
    }, 1000);
    return () => clearInterval(tick);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wizardStep, wizardPairingCode]);

  // QR Code: countdown + auto-refresh a cada 60s
  useEffect(() => {
    if (wizardStep !== 3 || !wizardQrCode) return;
    setQrExpireCountdown(60);
    const tick = setInterval(() => {
      setQrExpireCountdown((c) => {
        if (c <= 1) {
          // Regenerar QR Code automaticamente
          createInstanceMutation.mutate({
            salonId,
            instanceName: wizardInstance || evoInstance || undefined,
          });
          return 60;
        }
        return c - 1;
      });
    }, 1000);
    return () => clearInterval(tick);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wizardStep, wizardQrCode]);

  // Polling: check connection status every 5s while QR code or Pairing Code is shown
  useEffect(() => {
    if (!pollingActive) return;
    const interval = setInterval(async () => {
      try {
        const res = await fetch(`/api/trpc/superAdmin.getEvolutionInstanceStatus?input=${encodeURIComponent(JSON.stringify({ json: { salonId } }))}`, {
          credentials: "include",
        });
        const json = await res.json() as any;
        const state = json?.result?.data?.json?.state ?? json?.result?.data?.state;
        const webhookConfigured = json?.result?.data?.json?.webhookConfigured ?? json?.result?.data?.webhookConfigured;
        if (state === "open") {
          setWizardConnected(true);
          setPollingActive(false);
          setWizardStep(4);
          if (webhookConfigured === false) {
            toast.error("WhatsApp conectado, mas o webhook não pôde ser ativado automaticamente.");
          } else {
            toast.success("✅ WhatsApp conectado e webhook ativado!");
          }
          // Notificar o dono do salão via WhatsApp
          notifyConnectionMutation.mutate({ salonId, method: wizardConnectMode });
        }
      } catch { /* ignore */ }
    }, 5000);
    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pollingActive, salonId]);

  const createInstanceMutation = trpc.superAdmin.createEvolutionInstance.useMutation({
    onSuccess: (data) => {
      if (data.qrText || data.qrcode) {
        setWizardQrText(data.qrText ?? null);
        setWizardQrCode(data.qrcode ?? null);
        setWizardPairingCode(data.pairingCode ?? null);
        setPollingActive(true);
        setWizardStep(3);
        if (data.alreadyExisted) {
          toast.info("♻️ Instância já existia e foi reutilizada. Escaneie o QR Code.");
        } else {
          toast.info("✅ Instância criada! Escaneie o QR Code com o WhatsApp.");
        }
      } else if (data.state === "open") {
        setWizardConnected(true);
        setWizardStep(4);
        toast.success("✅ Instância já conectada!");
      } else {
        // Instância já existia mas sem QR Code disponível ainda — avançar para passo 3 para buscar QR
        setPollingActive(true);
        setWizardStep(3);
        if (data.alreadyExisted) {
          toast.info("♻️ Instância já existia. Aguardando QR Code...");
        }
      }
      utils.superAdmin.getSalonDetail.invalidate({ salonId });
    },
    onError: (e) => toast.error(e.message),
  });

  const setWebhookMutation = trpc.superAdmin.setEvolutionWebhook.useMutation({
    onSuccess: (data) => {
      toast.success(`Webhook configurado: ${data.webhookUrl}`);
      utils.superAdmin.getSalonDetail.invalidate({ salonId });
    },
    onError: (e) => toast.error(e.message),
  });

  const updateEvoMutation = trpc.superAdmin.updateEvolutionConfig.useMutation({
    onSuccess: () => {
      toast.success("Configuração salva com sucesso");
      utils.superAdmin.getSalonDetail.invalidate({ salonId });
    },
    onError: (e) => toast.error(e.message),
  });

  const testEvoMutation = trpc.superAdmin.testEvolutionConfig.useMutation({
    onSuccess: (data) => {
      setEvoStatus(data.connected ? "connected" : "disconnected");
      toast[data.connected ? "success" : "warning"](
        data.connected ? `Instância conectada (state: ${data.state})` : `Instância desconectada (state: ${data.state})`
      );
    },
    onError: (e) => {
      setEvoStatus("disconnected");
      toast.error(e.message);
    },
  });

  const reconnectEvoMutation = trpc.superAdmin.reconnectEvolution.useMutation({
    onSuccess: (data) => {
      if (data.qrText || data.qrcode) {
        setEvoQrText(data.qrText ?? null);
        setEvoQrCode(data.qrcode ?? null);
        setEvoPairingCode(data.pairingCode ?? null);
        setEvoPollingActive(true);
        setEvoQrCountdown(60);
        toast.info("QR Code gerado. Escaneie com o WhatsApp para conectar.");
      } else if (data.state === "open") {
        setEvoStatus("connected");
        toast.success("✅ Já conectado!");
      } else {
        // Sem QR Code ainda — ativar polling para aguardar
        setEvoPollingActive(true);
        toast.info("Aguardando QR Code... verifique em alguns segundos.");
      }
    },
    onError: (e) => toast.error(e.message),
  });

  // Countdown do QR Code de reconexão: 60s, renova automaticamente ao chegar em 0
  const reconnectEvoMutationRef = React.useRef(reconnectEvoMutation);
  reconnectEvoMutationRef.current = reconnectEvoMutation;
  useEffect(() => {
    if (!evoQrCode && !evoQrText) return;
    setEvoQrCountdown(60);
    const tick = setInterval(() => {
      setEvoQrCountdown((c) => {
        if (c <= 1) {
          // Renovar QR Code automaticamente
          reconnectEvoMutationRef.current.mutate({ salonId });
          return 60;
        }
        return c - 1;
      });
    }, 1000);
    return () => clearInterval(tick);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [evoQrCode, evoQrText]);

  const notifyConnectionMutation = trpc.superAdmin.notifyWhatsAppConnected.useMutation({
    onError: () => { /* silencioso — notificação é best-effort */ },
  });

  const deleteMutation = trpc.superAdmin.deleteAiAgent.useMutation({
    onSuccess: () => {
      toast.success("Agente removido com sucesso");
      utils.superAdmin.getAiAgent.invalidate({ salonId });
    },
    onError: (e) => toast.error(e.message),
  });
  const togglePausedMutation = (trpc as any).superAdmin.toggleAgentPaused.useMutation({
    onSuccess: (data: any) => {
      toast.success(data.paused ? "Agente pausado" : "Agente retomado com sucesso");
      utils.superAdmin.getAiAgent.invalidate({ salonId });
      utils.superAdmin.getSalonDetail.invalidate({ salonId });
    },
    onError: (e: any) => toast.error(e.message),
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Status do agente */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base font-medium flex items-center gap-2">
            <Zap className="w-4 h-4" />
            Agente IA Nativo
          </CardTitle>
        </CardHeader>
        <CardContent>
          {agent ? (
            <div className="space-y-4">
              <div className="flex items-center gap-3 p-3 bg-green-50 dark:bg-green-950/30 rounded-lg border border-green-200 dark:border-green-800">
                <CheckCircle2 className="w-5 h-5 text-green-600 shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-green-800 dark:text-green-200">
                    {agent.name}
                  </p>
                  <p className="text-xs text-green-600 dark:text-green-400 mt-0.5">
                    Tom: {agent.tone ?? "friendly"} &middot; ID: {agent.id}
                  </p>
                </div>
                <Badge className={agent.isActive ? "bg-green-100 text-green-700 border-green-200" : "bg-gray-100 text-gray-600 border-gray-200"}>
                  {agent.isActive ? "Ativo" : "Inativo"}
                </Badge>
                <Button
                  size="sm"
                  variant="outline"
                  className={salonDetail?.agentPaused ? "border-green-500 text-green-700 hover:bg-green-50" : "border-amber-500 text-amber-700 hover:bg-amber-50"}
                  onClick={() => togglePausedMutation.mutate({
                    salonId,
                    paused: !salonDetail?.agentPaused,
                    reason: salonDetail?.agentPaused ? undefined : "pausado pelo super admin",
                  })}
                  disabled={togglePausedMutation.isPending}
                >
                  {togglePausedMutation.isPending ? (
                    <Loader2 className="w-3 h-3 animate-spin" />
                  ) : salonDetail?.agentPaused ? (
                    <><Play className="w-3 h-3 mr-1" />Retomar</>
                  ) : (
                    <><Pause className="w-3 h-3 mr-1" />Pausar</>
                  )}
                </Button>
              </div>

              {/* Capacidades */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {[
                  { label: "Agendar", value: agent.canBook },
                  { label: "Cancelar", value: agent.canCancel },
                  { label: "Reagendar", value: agent.canReschedule },
                  { label: "FAQ", value: agent.canAnswerFaq },
                ].map(({ label, value }) => (
                  <div key={label} className={`flex items-center gap-1.5 text-xs px-2 py-1.5 rounded border ${
                    value ? "bg-green-50 border-green-200 text-green-700" : "bg-gray-50 border-gray-200 text-gray-500"
                  }`}>
                    {value ? <CheckCircle2 className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
                    {label}
                  </div>
                ))}
              </div>

              {/* Webhook URL */}
              <div className="p-3 bg-muted/40 rounded-lg border border-border/50 space-y-1.5">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Webhook WhatsApp</p>
                <code className="text-xs font-mono text-foreground break-all">
                  POST {window.location.origin}/api/agent/webhook/{salonId}
                </code>
                <p className="text-xs text-muted-foreground">
                  Token de verificação Meta: <code className="bg-muted px-1 rounded">salon-{salonId}</code>
                </p>
              </div>

              {/* Guia de integração do webhook */}
              <div className="p-4 bg-blue-50 dark:bg-blue-950/20 rounded-lg border border-blue-200 dark:border-blue-800 space-y-3">
                <p className="text-xs font-semibold text-blue-800 dark:text-blue-200 uppercase tracking-wide flex items-center gap-1.5">
                  <MessageSquare className="w-3.5 h-3.5" />
                  Guia de Integração WhatsApp
                </p>
                <ol className="space-y-2 text-xs text-blue-700 dark:text-blue-300">
                  <li className="flex gap-2">
                    <span className="font-bold shrink-0">1.</span>
                    <span>Instale a <strong>Evolution API</strong> (self-hosted) ou use o <strong>Meta Cloud API</strong> no seu servidor.</span>
                  </li>
                  <li className="flex gap-2">
                    <span className="font-bold shrink-0">2.</span>
                    <span>Configure o webhook apontando para:</span>
                  </li>
                  <li className="ml-4">
                    <code className="block bg-blue-100 dark:bg-blue-900/40 px-2 py-1 rounded font-mono text-xs break-all">
                      POST {window.location.origin}/api/agent/webhook/{salonId}
                    </code>
                  </li>
                  <li className="flex gap-2">
                    <span className="font-bold shrink-0">3.</span>
                    <span>Para verificação Meta (GET), use o token: <code className="bg-blue-100 dark:bg-blue-900/40 px-1 rounded">salon-{salonId}</code></span>
                  </li>
                  <li className="flex gap-2">
                    <span className="font-bold shrink-0">4.</span>
                    <span>Payload esperado (Evolution API):</span>
                  </li>
                  <li className="ml-4">
                    <code className="block bg-blue-100 dark:bg-blue-900/40 px-2 py-1 rounded font-mono text-xs whitespace-pre">{`{"data":{"key":{"remoteJid":"5511999...@s.whatsapp.net"},"message":{"conversation":"Olá"}}}`}</code>
                  </li>
                  <li className="flex gap-2">
                    <span className="font-bold shrink-0">5.</span>
                    <span>O agente responderá automaticamente via <code className="bg-blue-100 dark:bg-blue-900/40 px-1 rounded">POST /send-message</code> da Evolution API.</span>
                  </li>
                </ol>
              </div>

              {/* Ações */}
              <div className="flex flex-wrap gap-2 pt-2">
                <Link href={`/superadmin/agentes`}>
                  <Button variant="outline" size="sm" className="gap-1.5">
                    <ExternalLink className="w-3.5 h-3.5" />
                    Editar no Painel de Agentes
                  </Button>
                </Link>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="outline" size="sm" className="gap-1.5 text-destructive border-destructive/30 hover:bg-destructive/10">
                      <XCircle className="w-3.5 h-3.5" />
                      Remover Agente
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Remover agente nativo?</AlertDialogTitle>
                      <AlertDialogDescription>
                        O agente <strong>{agent.name}</strong> e todo o histórico de conversas serão removidos permanentemente.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancelar</AlertDialogCancel>
                      <AlertDialogAction
                        className="bg-destructive hover:bg-destructive/90"
                        onClick={() => deleteMutation.mutate({ id: agent.id })}
                      >
                        Remover
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center gap-3 p-3 bg-muted/40 rounded-lg border border-border/50">
                <AlertCircle className="w-5 h-5 text-muted-foreground shrink-0" />
                <div>
                  <p className="text-sm font-medium">Nenhum agente nativo configurado</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Crie um agente no painel de agentes e associe-o a este salão.
                  </p>
                </div>
              </div>
              <Link href="/superadmin/agentes">
                <Button size="sm" className="gap-1.5">
                  <Zap className="w-3.5 h-3.5" />
                  Criar Agente no Painel
                </Button>
              </Link>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ─── Evolution API Wizard ─────────────────────────────────────────── */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base font-medium flex items-center gap-2">
            <Wifi className="w-4 h-4" />
            Configuração Evolution API (WhatsApp)
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">

          {/* ── Se já configurado, mostrar status + opção de editar manualmente ── */}
          {isAlreadyConfigured && !showManual ? (
            <div className="space-y-3">
              {/* Status atual */}
              {evoStatus !== "idle" && (
                <div className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium ${
                  evoStatus === "connected"
                    ? "bg-green-50 text-green-700 border border-green-200 dark:bg-green-950/30 dark:text-green-300 dark:border-green-800"
                    : "bg-red-50 text-red-700 border border-red-200 dark:bg-red-950/30 dark:text-red-300 dark:border-red-800"
                }`}>
                  {evoStatus === "connected" ? <Wifi className="w-4 h-4" /> : <WifiOff className="w-4 h-4" />}
                  {evoStatus === "connected" ? "Instância conectada" : "Instância desconectada ou inacessível"}
                </div>
              )}

              <div className="p-3 bg-muted/40 rounded-lg border border-border/50 space-y-1.5">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Instância configurada</p>
                <p className="text-sm font-mono">{evoInstance || "—"}</p>
                <p className="text-xs text-muted-foreground">{evoUrl || "—"}</p>
              </div>

              <div className="flex flex-wrap gap-2">
                <Button
                  size="sm" variant="outline" className="gap-1.5"
                  disabled={testEvoMutation.isPending}
                  onClick={() => { setEvoQrCode(null); testEvoMutation.mutate({ salonId }); }}
                >
                  {testEvoMutation.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <TestTube2 className="w-3.5 h-3.5" />}
                  Testar Conexão
                </Button>
                <Button
                  size="sm" variant="outline"
                  className={`gap-1.5 ${
                    evoStatus === "disconnected"
                      ? "border-amber-300 text-amber-700 hover:bg-amber-50"
                      : "border-blue-300 text-blue-700 hover:bg-blue-50"
                  }`}
                  disabled={reconnectEvoMutation.isPending}
                  onClick={() => {
                    setEvoQrCode(null);
                    setEvoQrText(null);
                    setEvoPairingCode(null);
                    reconnectEvoMutation.mutate({ salonId });
                  }}
                >
                  {reconnectEvoMutation.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                  {evoStatus === "disconnected" ? "Reconectar" : "Gerar novo QR Code"}
                </Button>
                <Button size="sm" variant="ghost" className="gap-1.5 text-muted-foreground" onClick={() => setShowManual(true)}>
                  <Settings className="w-3.5 h-3.5" />
                  Editar manualmente
                </Button>
              </div>

              {/* QR Code / Pairing Code para reconexão */}
              {(evoQrText || evoQrCode || evoPairingCode) && (
                <div className="mt-3 p-4 rounded-lg flex flex-col items-center gap-3" style={{background:'#fff',border:'1px solid #e5e7eb'}}>
                  {/* Toggle */}
                  <div className="flex gap-2 w-full max-w-xs">
                    <Button
                      size="sm"
                      variant={evoConnectMode === "qr" ? "default" : "outline"}
                      className="flex-1 gap-1.5"
                      onClick={() => setEvoConnectMode("qr")}
                    >
                      <QrCode className="w-3.5 h-3.5" />
                      QR Code
                    </Button>
                    <Button
                      size="sm"
                      variant={evoConnectMode === "pairing" ? "default" : "outline"}
                      className="flex-1 gap-1.5"
                      onClick={() => setEvoConnectMode("pairing")}
                      disabled={!evoPairingCode}
                    >
                      <span className="text-base leading-none">#</span>
                      Código
                    </Button>
                  </div>

                  {evoConnectMode === "qr" ? (
                    <div className="flex flex-col items-center gap-2">
                      <div style={{background:'#ffffff',padding:'20px',borderRadius:'8px',display:'inline-block',position:'relative'}}>
                        {evoQrCode ? (
                          <img
                            src={evoQrCode.startsWith("data:") ? evoQrCode : `data:image/png;base64,${evoQrCode}`}
                            alt="QR Code WhatsApp"
                            style={{width:288,height:288,display:'block',background:'#ffffff',imageRendering:'pixelated'}}
                          />
                        ) : evoQrText ? (
                          <QRCodeCanvas
                            value={evoQrText}
                            size={288}
                            level="L"
                            fgColor="#000000"
                            bgColor="#ffffff"
                            includeMargin={true}
                          />
                        ) : null}
                      </div>
                      {/* Ações: Compartilhar + Copiar link */}
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          className="gap-1.5 text-xs"
                          onClick={async () => {
                            const src = evoQrCode
                              ? (evoQrCode.startsWith("data:") ? evoQrCode : `data:image/png;base64,${evoQrCode}`)
                              : null;
                            if (src && navigator.share) {
                              try {
                                const res = await fetch(src);
                                const blob = await res.blob();
                                const file = new File([blob], "qrcode-whatsapp.png", { type: "image/png" });
                                await navigator.share({ title: "QR Code WhatsApp", files: [file] });
                              } catch { toast.error("Não foi possível compartilhar."); }
                            } else if (src) {
                              const a = document.createElement("a");
                              a.href = src;
                              a.download = "qrcode-whatsapp.png";
                              a.click();
                              toast.success("QR Code salvo!");
                            } else {
                              toast.error("QR Code ainda não disponível.");
                            }
                          }}
                        >
                          <Share2 className="w-3.5 h-3.5" />
                          Compartilhar
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="gap-1.5 text-xs"
                          onClick={() => {
                            const instanceUrl = `${window.location.origin}/s/${salonDetail?.slug ?? ""}`;
                            navigator.clipboard.writeText(instanceUrl).then(() => toast.success("Link copiado!"));
                          }}
                        >
                          <Copy className="w-3.5 h-3.5" />
                          Copiar link
                        </Button>
                      </div>
                      {/* Countdown de expiração do QR Code */}
                      <div className="flex items-center gap-2 text-xs">
                        <span className={`font-medium px-2 py-0.5 rounded-full ${
                          evoQrCountdown <= 10 ? "bg-red-100 text-red-600" : "bg-gray-100 text-gray-600"
                        }`}>
                          {evoQrCountdown}s
                        </span>
                        <span className="text-muted-foreground">
                          {evoQrCountdown <= 10 ? "Renovando QR Code..." : `QR Code expira em ${evoQrCountdown}s — renova automaticamente`}
                        </span>
                      </div>
                    </div>
                  ) : (
                    evoPairingCode ? (
                      <div className="flex flex-col items-center gap-3">
                        <div className="rounded-xl border-2 border-primary/30 bg-primary/5 px-8 py-5 text-center w-full max-w-xs">
                          <p className="text-xs text-muted-foreground mb-2">Código de pareamento</p>
                          <p className="text-4xl font-mono font-bold tracking-[0.25em] text-primary select-all">
                            {evoPairingCode.length === 8
                              ? `${evoPairingCode.slice(0,4)}-${evoPairingCode.slice(4)}`
                              : evoPairingCode}
                          </p>
                          {/* Countdown + Copiar */}
                          <div className="flex items-center justify-center gap-3 mt-3">
                            <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                              evoPairingCountdown <= 10 ? "bg-red-100 text-red-600" : "bg-gray-100 text-gray-600"
                            }`}>
                              {evoPairingCountdown}s
                            </span>
                            <button
                              className="flex items-center gap-1 text-xs text-primary hover:underline"
                              onClick={() => {
                                const code = evoPairingCode.length === 8
                                  ? `${evoPairingCode.slice(0,4)}-${evoPairingCode.slice(4)}`
                                  : evoPairingCode;
                                navigator.clipboard.writeText(code);
                                setEvoCopied(true);
                                setTimeout(() => setEvoCopied(false), 2000);
                              }}
                            >
                              {evoCopied ? "✓ Copiado!" : "📋 Copiar código"}
                            </button>
                          </div>
                        </div>
                        <div className="text-xs text-muted-foreground space-y-1 text-left max-w-xs">
                          <p className="font-medium text-foreground">Como usar:</p>
                          <p>1. Abra o <strong>WhatsApp Web</strong> ou <strong>WhatsApp para Windows</strong></p>
                          <p>2. Clique em <strong>Conectar com número de telefone</strong></p>
                          <p>3. Digite o número e clique em <strong>Avançar</strong></p>
                          <p>4. No celular, toque em <strong>Insira o código</strong> na notificação</p>
                          <p>5. Cole ou digite o código acima e confirme</p>
                        </div>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          Aguardando conexão... renova em {evoPairingCountdown}s
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-4 text-sm text-muted-foreground space-y-1">
                        <p className="font-medium text-foreground">Código de pareamento não disponível</p>
                        <p className="text-xs">Sua versão da Evolution API pode não suportar este recurso.</p>
                        <p className="text-xs">Clique em <strong>Reconectar</strong> para tentar novamente ou use o QR Code.</p>
                      </div>
                    )
                  )}

                  {evoPollingActive && (
                    <div className="flex items-center gap-1.5 text-xs text-green-600">
                      <Loader2 className="w-3 h-3 animate-spin" />
                      Aguardando conexão...
                    </div>
                  )}
                  <Button size="sm" variant="ghost" className="text-xs text-gray-500" onClick={() => { setEvoQrCode(null); setEvoQrText(null); setEvoPairingCode(null); setEvoPollingActive(false); }}>
                    Fechar
                  </Button>
                </div>
              )}
            </div>
          ) : showManual ? (
            /* ── Edição manual (para salões já configurados) ── */
            <div className="space-y-4">
              <Button size="sm" variant="ghost" className="gap-1 text-muted-foreground -ml-1" onClick={() => setShowManual(false)}>
                <ArrowLeft className="w-3.5 h-3.5" /> Voltar
              </Button>
              <div className="grid gap-3">
                <div className="space-y-1.5">
                  <Label className="text-xs">URL da Evolution API</Label>
                  <Input placeholder="https://evolution.seudominio.com" value={evoUrl} onChange={(e) => setEvoUrl(e.target.value)} className="text-sm" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Nome da Instância</Label>
                  <Input placeholder="minha-instancia" value={evoInstance} onChange={(e) => setEvoInstance(e.target.value)} className="text-sm" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">API Key</Label>
                  <Input placeholder="sua-api-key-aqui" type="password" value={evoKey} onChange={(e) => setEvoKey(e.target.value)} className="text-sm" />
                </div>
              </div>
              <div className="flex gap-2">
                <Button
                  size="sm" className="gap-1.5"
                  disabled={updateEvoMutation.isPending}
                  onClick={() => updateEvoMutation.mutate({ salonId, evolutionApiUrl: evoUrl || undefined, evolutionInstance: evoInstance || undefined, evolutionApiKey: evoKey || undefined })}
                >
                  {updateEvoMutation.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                  Salvar
                </Button>
                <Button size="sm" variant="outline" onClick={() => setShowManual(false)}>Cancelar</Button>
              </div>
            </div>
          ) : (
            /* ── Wizard de configuração (salões sem Evolution configurada) ── */
            <div className="space-y-5">
              {/* Barra de progresso dos passos */}
              <div className="flex items-center gap-1">
                {([1, 2, 3, 4] as const).map((step) => (
                  <div key={step} className="flex items-center gap-1 flex-1">
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold shrink-0 transition-colors ${
                      wizardStep > step
                        ? "bg-green-500 text-white"
                        : wizardStep === step
                          ? "bg-primary text-primary-foreground"
                          : "bg-muted text-muted-foreground"
                    }`}>
                      {wizardStep > step ? "✓" : step}
                    </div>
                    {step < 4 && <div className={`h-0.5 flex-1 transition-colors ${wizardStep > step ? "bg-green-400" : "bg-border"}`} />}
                  </div>
                ))}
              </div>

              {/* Passo 1 — Credenciais */}
              {wizardStep === 1 && (
                <div className="space-y-4">
                  <div>
                    <h3 className="text-sm font-semibold">Passo 1 — Credenciais da Evolution API</h3>
                    <p className="text-xs text-muted-foreground mt-1">Informe a URL do servidor Evolution API e a API Key global (Global API Key). O nome da instância será usado para identificar este salão.</p>
                  </div>
                  <div className="grid gap-3">
                    <div className="space-y-1.5">
                      <Label className="text-xs">URL da Evolution API <span className="text-destructive">*</span></Label>
                      <Input
                        placeholder="https://evolution.seudominio.com"
                        value={wizardUrl}
                        onChange={(e) => setWizardUrl(e.target.value)}
                        className="text-sm"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs">API Key (Global) <span className="text-destructive">*</span></Label>
                      <Input
                        placeholder="sua-api-key-global"
                        type="password"
                        value={wizardKey}
                        onChange={(e) => setWizardKey(e.target.value)}
                        className="text-sm"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs">Nome da Instância <span className="text-destructive">*</span></Label>
                      <Input
                        placeholder={`salon-${salonId}`}
                        value={wizardInstance}
                        onChange={(e) => setWizardInstance(e.target.value)}
                        className="text-sm"
                      />
                      <p className="text-xs text-muted-foreground">Use apenas letras minúsculas, números e hífens.</p>
                    </div>
                  </div>
                  <Button
                    size="sm" className="gap-1.5 w-full"
                    disabled={!wizardUrl.trim() || !wizardKey.trim() || !wizardInstance.trim()}
                    onClick={() => {
                      // Salvar credenciais no banco antes de criar instância
                      updateEvoMutation.mutate({
                        salonId,
                        evolutionApiUrl: wizardUrl,
                        evolutionInstance: wizardInstance,
                        evolutionApiKey: wizardKey,
                      }, {
                        onSuccess: () => setWizardStep(2),
                      });
                    }}
                  >
                    {updateEvoMutation.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
                    Próximo: Criar Instância
                  </Button>
                </div>
              )}

              {/* Passo 2 — Criar instância */}
              {wizardStep === 2 && (
                <div className="space-y-4">
                  <div>
                    <h3 className="text-sm font-semibold">Passo 2 — Criar Instância WhatsApp</h3>
                    <p className="text-xs text-muted-foreground mt-1">
                      Clique no botão abaixo para criar a instância <strong>{wizardInstance || evoInstance}</strong> na Evolution API.
                      Se a instância já existir, ela será reutilizada.
                    </p>
                  </div>
                  <div className="p-3 bg-muted/40 rounded-lg border border-border/50 space-y-1">
                    <p className="text-xs text-muted-foreground">Servidor</p>
                    <p className="text-sm font-mono">{wizardUrl || evoUrl}</p>
                    <p className="text-xs text-muted-foreground mt-1">Instância</p>
                    <p className="text-sm font-mono">{wizardInstance || evoInstance}</p>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => setWizardStep(1)}>Voltar</Button>
                    <Button
                      size="sm" className="gap-1.5 flex-1"
                      disabled={createInstanceMutation.isPending}
                      onClick={() => createInstanceMutation.mutate({
                        salonId,
                        // O servidor usa a chave validada e já persistida.
                        // Isso impede que uma aba aberta com chave antiga
                        // substitua a configuração atual ao criar/reusar a instância.
                        instanceName: wizardInstance || evoInstance || undefined,
                      })}
                    >
                      {createInstanceMutation.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wifi className="w-3.5 h-3.5" />}
                      Criar Instância
                    </Button>
                  </div>
                </div>
              )}

              {/* Passo 3 — QR Code / Pairing Code */}
              {wizardStep === 3 && (
                <div className="space-y-4">
                  <div>
                    <h3 className="text-sm font-semibold">Passo 3 — Conectar WhatsApp</h3>
                    <p className="text-xs text-muted-foreground mt-1">
                      Escolha como deseja conectar o WhatsApp.
                    </p>
                  </div>

                  {/* Toggle QR / Pairing Code */}
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant={wizardConnectMode === "qr" ? "default" : "outline"}
                      className="flex-1 gap-1.5"
                      onClick={() => setWizardConnectMode("qr")}
                    >
                      <QrCode className="w-3.5 h-3.5" />
                      QR Code
                    </Button>
                    <Button
                      size="sm"
                      variant={wizardConnectMode === "pairing" ? "default" : "outline"}
                      className="flex-1 gap-1.5"
                      onClick={() => setWizardConnectMode("pairing")}
                      disabled={!wizardPairingCode}
                    >
                      <span className="text-base leading-none">#</span>
                      Código de Pareamento
                    </Button>
                  </div>

                  {wizardConnectMode === "qr" ? (
                    (wizardQrText || wizardQrCode) ? (
                      <div className="flex flex-col items-center gap-3">
                        <div className="relative" style={{background:'#ffffff',padding:'20px',borderRadius:'12px',display:'inline-block',border:'2px solid #e5e7eb',boxShadow:'0 2px 8px rgba(0,0,0,0.08)'}}>
                          {wizardQrCode ? (
                            <img
                              src={wizardQrCode.startsWith("data:") ? wizardQrCode : `data:image/png;base64,${wizardQrCode}`}
                              alt="QR Code WhatsApp"
                              style={{width:288,height:288,display:'block',background:'#ffffff',imageRendering:'pixelated'}}
                            />
                          ) : wizardQrText ? (
                            <QRCodeCanvas
                              value={wizardQrText}
                              size={288}
                              level="L"
                              fgColor="#000000"
                              bgColor="#ffffff"
                              includeMargin={true}
                            />
                          ) : null}
                          {/* Countdown ring */}
                          <div className={`absolute top-2 right-2 w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${
                            qrExpireCountdown <= 10 ? "bg-red-100 text-red-600" : "bg-gray-100 text-gray-600"
                          }`}>
                            {qrExpireCountdown}
                          </div>
                        </div>
                        {/* Ações: Compartilhar + Copiar link */}
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            className="gap-1.5 text-xs"
                            onClick={async () => {
                              const src = wizardQrCode
                                ? (wizardQrCode.startsWith("data:") ? wizardQrCode : `data:image/png;base64,${wizardQrCode}`)
                                : null;
                              if (src && navigator.share) {
                                try {
                                  const res = await fetch(src);
                                  const blob = await res.blob();
                                  const file = new File([blob], "qrcode-whatsapp.png", { type: "image/png" });
                                  await navigator.share({ title: "QR Code WhatsApp", files: [file] });
                                } catch { toast.error("Não foi possível compartilhar."); }
                              } else if (src) {
                                const a = document.createElement("a");
                                a.href = src;
                                a.download = "qrcode-whatsapp.png";
                                a.click();
                                toast.success("QR Code salvo!");
                              } else {
                                toast.error("QR Code ainda não disponível.");
                              }
                            }}
                          >
                            <Share2 className="w-3.5 h-3.5" />
                            Compartilhar
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            className="gap-1.5 text-xs"
                            onClick={() => {
                            const instanceUrl = `${window.location.origin}/s/${salonDetail?.slug ?? ""}`;
                              navigator.clipboard.writeText(instanceUrl).then(() => toast.success("Link copiado!"));
                            }}
                          >
                            <Copy className="w-3.5 h-3.5" />
                            Copiar link
                          </Button>
                        </div>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          Aguardando conexão... QR renova em {qrExpireCountdown}s
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center justify-center py-8">
                        <Loader2 className="w-6 h-6 animate-spin text-primary" />
                      </div>
                    )
                  ) : (
                    /* Pairing Code mode */
                    wizardPairingCode ? (
                      <div className="flex flex-col items-center gap-4 py-2">
                        <div className="rounded-xl border-2 border-primary/30 bg-primary/5 px-8 py-5 text-center w-full max-w-xs">
                          <p className="text-xs text-muted-foreground mb-2">Código de pareamento</p>
                          <p className="text-4xl font-mono font-bold tracking-[0.25em] text-primary select-all">
                            {wizardPairingCode.length === 8
                              ? `${wizardPairingCode.slice(0,4)}-${wizardPairingCode.slice(4)}`
                              : wizardPairingCode}
                          </p>
                          {/* Countdown + Copiar */}
                          <div className="flex items-center justify-center gap-3 mt-3">
                            <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                              pairingCountdown <= 10 ? "bg-red-100 text-red-600" : "bg-gray-100 text-gray-600"
                            }`}>
                              {pairingCountdown}s
                            </span>
                            <button
                              className="flex items-center gap-1 text-xs text-primary hover:underline"
                              onClick={() => {
                                const code = wizardPairingCode.length === 8
                                  ? `${wizardPairingCode.slice(0,4)}-${wizardPairingCode.slice(4)}`
                                  : wizardPairingCode;
                                navigator.clipboard.writeText(code);
                                setWizardCopied(true);
                                setTimeout(() => setWizardCopied(false), 2000);
                              }}
                            >
                              {wizardCopied ? "✓ Copiado!" : "📋 Copiar código"}
                            </button>
                          </div>
                        </div>
                        <div className="text-xs text-muted-foreground space-y-1 text-left max-w-xs">
                          <p className="font-medium text-foreground">Como usar:</p>
                          <p>1. Abra o <strong>WhatsApp Web</strong> ou <strong>WhatsApp para Windows</strong></p>
                          <p>2. Clique em <strong>Conectar com número de telefone</strong></p>
                          <p>3. Digite o número do WhatsApp e clique em <strong>Avançar</strong></p>
                          <p>4. No celular principal, toque em <strong>Insira o código</strong> na notificação</p>
                          <p>5. Cole ou digite o código acima e confirme</p>
                        </div>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          Aguardando conexão... renova em {pairingCountdown}s
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-6 text-sm text-muted-foreground space-y-2">
                        <p className="font-medium text-foreground">Código de pareamento não disponível</p>
                        <p className="text-xs">Sua versão da Evolution API pode não suportar este recurso, ou o Pairing Code ainda não foi gerado.</p>
                        <p className="text-xs">Clique em <strong>Gerar novo QR</strong> abaixo para tentar novamente, ou use o QR Code.</p>
                      </div>
                    )
                  )}

                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => { setPollingActive(false); setWizardStep(2); }}>
                      Voltar
                    </Button>
                    <Button
                      size="sm" variant="outline" className="gap-1.5"
                      disabled={createInstanceMutation.isPending}
                      onClick={() => createInstanceMutation.mutate({
                        salonId,
                        instanceName: wizardInstance || evoInstance || undefined,
                      })}
                    >
                      {createInstanceMutation.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                      Gerar novo QR
                    </Button>
                    <Button
                      size="sm" className="gap-1.5 flex-1"
                      onClick={() => {
                        setWebhookMutation.mutate({ salonId, webhookBaseUrl: window.location.origin });
                        setWizardStep(4);
                      }}
                    >
                      Já conectei — Configurar Webhook
                    </Button>
                  </div>
                </div>
              )}

              {/* Passo 4 — Webhook + Conclusão */}
              {wizardStep === 4 && (
                <div className="space-y-4">
                  <div>
                    <h3 className="text-sm font-semibold">Passo 4 — Configurar Webhook</h3>
                    <p className="text-xs text-muted-foreground mt-1">
                      O webhook será configurado automaticamente para que o agente receba mensagens do WhatsApp.
                    </p>
                  </div>

                  {wizardConnected && (
                    <div className="flex items-center gap-2 p-3 bg-green-50 dark:bg-green-950/30 rounded-lg border border-green-200 dark:border-green-800">
                      <CheckCircle2 className="w-5 h-5 text-green-600 shrink-0" />
                      <p className="text-sm text-green-700 dark:text-green-300 font-medium">WhatsApp conectado com sucesso!</p>
                    </div>
                  )}

                  <div className="p-3 bg-muted/40 rounded-lg border border-border/50 space-y-1.5">
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Webhook URL</p>
                    <code className="text-xs font-mono text-foreground break-all">
                      POST {window.location.origin}/api/agent/webhook/{salonId}
                    </code>
                  </div>

                  <Button
                    size="sm" className="gap-1.5 w-full"
                    disabled={setWebhookMutation.isPending}
                    onClick={() => setWebhookMutation.mutate({ salonId, webhookBaseUrl: window.location.origin })}
                  >
                    {setWebhookMutation.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
                    Configurar Webhook Automaticamente
                  </Button>

                  {setWebhookMutation.isSuccess && (
                    <div className="p-4 bg-green-50 dark:bg-green-950/20 rounded-lg border border-green-200 dark:border-green-800 space-y-2">
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="w-5 h-5 text-green-600 shrink-0" />
                        <p className="text-sm font-semibold text-green-800 dark:text-green-200">Configuração concluída!</p>
                      </div>
                      <p className="text-xs text-green-700 dark:text-green-300">
                        A instância Evolution API está configurada e o webhook está ativo. O agente já pode receber e responder mensagens no WhatsApp.
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Histórico de Conexões */}
      {connLogs && connLogs.length > 0 && (
        <Card>
          <CardContent className="pt-4 pb-3">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">Histórico de Conexões</p>
            <div className="space-y-1.5">
              {connLogs.map((log: any) => (
                <div key={log.id} className="flex items-center justify-between text-xs">
                  <div className="flex items-center gap-2">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-medium ${
                      log.method === "pairing" ? "bg-blue-100 text-blue-700" : "bg-green-100 text-green-700"
                    }`}>
                      {log.method === "pairing" ? "# Código" : "📷 QR Code"}
                    </span>
                    <span className="text-muted-foreground">
                      {safeFormat(log.connectedAt, "dd/MM/yyyy, HH:mm")}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

export default function SuperAdminSalonDetail() {
  const [, params] = useRoute("/superadmin/saloes/:id");
  const salonId = params ? Number(params.id) : null;

  const [activeTab, setActiveTab] = useState("services");
  const [apptPage, setApptPage] = useState(1);
  const [apptStatus, setApptStatus] = useState<string | undefined>(undefined);
  const [impersonating, setImpersonating] = useState(false);
  const [zaiaAgentIdInput, setZaiaAgentIdInput] = useState("");
  const [updateBehavior, setUpdateBehavior] = useState(true);
  const [, navigate] = useLocation();
  const handleImpersonate = async () => {
    if (!salonId) return;
    setImpersonating(true);
    try {
      const res = await fetch(`/api/impersonate/${salonId}`, { method: "POST", credentials: "include" });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error ?? "Erro ao entrar como salão");
      toast.success(`Entrando como ${data.salonName}`, { description: "Você está agora no painel do salão. Clique em 'Sair da sessão' para voltar." });
      setTimeout(() => { window.location.href = "/admin"; }, 800);
    } catch (e: any) {
      toast.error("Erro ao entrar como salão", { description: e.message });
      setImpersonating(false);
    }
  };

  const { data: detail, isLoading: detailLoading } = trpc.superAdmin.getSalonDetail.useQuery(
    { salonId: salonId! },
    { enabled: salonId !== null }
  );

  const { data: branches, isLoading: branchesLoading } = (trpc as any).branches?.listBySalonAdmin?.useQuery(
    { salonId: salonId! },
    { enabled: salonId !== null }
  ) ?? { data: undefined, isLoading: false };

  const { data: salonFeatures } = trpc.superAdmin.getSalonFeatures.useQuery(
    { salonId: salonId! },
    { enabled: salonId !== null }
  );

  const updateFeaturesMutation = trpc.superAdmin.updateSalonFeatures.useMutation({
    onSuccess: () => toast.success("Feature atualizada!"),
    onError: (e) => toast.error(e.message),
  });

  const utils = trpc.useUtils();

  const linkZaiaMutation = (trpc.superAdmin as any).linkZaiaAgent?.useMutation({
    onSuccess: (data: any) => {
      toast.success(`Agente vinculado com sucesso!`, {
        description: `${data.agentName} (ID: ${data.agentId}) vinculado ao salão.${data.behaviorUpdated ? " Behavior atualizado." : ""}`,
      });
      setZaiaAgentIdInput("");
      utils.superAdmin.getSalonDetail.invalidate({ salonId: salonId! });
    },
    onError: (e: any) => toast.error("Erro ao vincular agente", { description: e.message }),
  });

  const unlinkZaiaMutation = (trpc.superAdmin as any).unlinkZaiaAgent?.useMutation({
    onSuccess: (data: any) => {
      toast.success("Agente desvinculado", {
        description: data.deletedFromZaia
          ? `Agente #${data.agentId} desvinculado e deletado da Zaia.`
          : `Agente #${data.agentId} desvinculado (mantido na Zaia).`,
      });
      utils.superAdmin.getSalonDetail.invalidate({ salonId: salonId! });
    },
    onError: (e: any) => toast.error("Erro ao desvincular agente", { description: e.message }),
  });

  const { data: appointments, isLoading: apptLoading } = trpc.superAdmin.getSalonAppointments.useQuery(
    {
      salonId: salonId!,
      page: apptPage,
      pageSize: 20,
      status: apptStatus as any,
    },
    { enabled: salonId !== null }
  );

  if (!salonId) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-muted-foreground">ID de salão inválido.</p>
      </div>
    );
  }

  if (detailLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!detail) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
          <p className="text-muted-foreground">Salão não encontrado.</p>
          <Link href="/superadmin">
            <Button variant="outline" className="mt-4">
              <ArrowLeft className="w-4 h-4 mr-2" /> Voltar
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  const salon = (detail as any).salon;
  const services = (detail as any).services ?? [];
  const professionals = (detail as any).professionals ?? [];
  const staff = (detail as any).staff ?? [];

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Link href="/superadmin">
            <Button variant="ghost" size="sm">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Voltar ao Super Admin
            </Button>
          </Link>
        </div>

        {/* Salon Info Card */}
        <Card className="mb-6 border-border/50">
          <CardContent className="p-6">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-amber-100 rounded-2xl flex items-center justify-center">
                  <Building2 className="w-7 h-7 text-amber-600" />
                </div>
                <div>
                  <div className="flex items-center gap-3">
                    <h1 className="text-2xl font-semibold text-foreground">{salon.name}</h1>
                    {STATUS_BADGE[salon.status] ?? <Badge variant="outline">{salon.status}</Badge>}
                  </div>
                  {salon.description && (
                    <p className="text-muted-foreground text-sm mt-1">{salon.description}</p>
                  )}
                  <div className="flex items-center gap-4 mt-2 flex-wrap">
                    {salon.phone && (
                      <span className="flex items-center gap-1 text-sm text-muted-foreground">
                        <Phone className="w-3.5 h-3.5" /> {salon.phone}
                      </span>
                    )}
                    {salon.email && (
                      <span className="flex items-center gap-1 text-sm text-muted-foreground">
                        <Mail className="w-3.5 h-3.5" /> {salon.email}
                      </span>
                    )}
                    {salon.address && (
                      <span className="flex items-center gap-1 text-sm text-muted-foreground">
                        <MapPin className="w-3.5 h-3.5" /> {salon.address}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex flex-col items-end gap-4">
                <Button
                  onClick={handleImpersonate}
                  disabled={impersonating}
                  className="bg-amber-600 hover:bg-amber-700 text-white gap-2"
                  size="sm"
                >
                  {impersonating ? <Loader2 className="w-4 h-4 animate-spin" /> : <LogIn className="w-4 h-4" />}
                  Entrar como salão
                </Button>
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <p className="text-2xl font-bold text-foreground">{services.length}</p>
                    <p className="text-xs text-muted-foreground">Serviços</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-foreground">{professionals.length}</p>
                    <p className="text-xs text-muted-foreground">Profissionais</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-foreground">{staff.length}</p>
                    <p className="text-xs text-muted-foreground">Equipe</p>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          {/* Mobile: caixa suspensa */}
          <div className="block md:hidden mb-4">
            <Select value={activeTab} onValueChange={setActiveTab}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Selecione a seção" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="services">Serviços ({services.length})</SelectItem>
                <SelectItem value="professionals">Profissionais ({professionals.length})</SelectItem>
                <SelectItem value="staff">Equipe ({staff.length})</SelectItem>
                <SelectItem value="appointments">Agendamentos</SelectItem>
                <SelectItem value="branches">Filiais</SelectItem>
                <SelectItem value="zaia">Agente Zaia</SelectItem>
                <SelectItem value="native-agent">Agente IA Nativo</SelectItem>
                <SelectItem value="config">Configurações</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {/* Desktop: abas normais */}
          <TabsList className="hidden md:inline-flex mb-6">
            <TabsTrigger value="services">
              <Scissors className="w-4 h-4 mr-2" />
              Serviços ({services.length})
            </TabsTrigger>
            <TabsTrigger value="professionals">
              <Star className="w-4 h-4 mr-2" />
              Profissionais ({professionals.length})
            </TabsTrigger>
            <TabsTrigger value="staff">
              <Users className="w-4 h-4 mr-2" />
              Equipe ({staff.length})
            </TabsTrigger>
            <TabsTrigger value="appointments">
              <Calendar className="w-4 h-4 mr-2" />
              Agendamentos
            </TabsTrigger>
            <TabsTrigger value="branches">
              Filiais
            </TabsTrigger>
            <TabsTrigger value="zaia">
              <Bot className="w-4 h-4 mr-2" />
              Agente Zaia
            </TabsTrigger>
            <TabsTrigger value="native-agent">
              <Zap className="w-4 h-4 mr-2" />
              Agente IA Nativo
            </TabsTrigger>
            <TabsTrigger value="config">
              <Settings className="w-4 h-4 mr-2" />
              Configurações
            </TabsTrigger>
          </TabsList>

          {/* Serviços */}
          <TabsContent value="services">
            <Card>
              <CardHeader>
                <CardTitle className="text-base font-medium">Serviços Cadastrados</CardTitle>
              </CardHeader>
              <CardContent>
                {!services.length ? (
                  <div className="text-center py-10">
                    <Scissors className="w-10 h-10 text-muted-foreground mx-auto mb-2" />
                    <p className="text-muted-foreground">Nenhum serviço cadastrado.</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Serviço</TableHead>
                        <TableHead>Duração</TableHead>
                        <TableHead>Preço</TableHead>
                        <TableHead>Categoria</TableHead>
                        <TableHead>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {services.map((s: any) => (
                        <TableRow key={s.id}>
                          <TableCell>
                            <div>
                              <p className="font-medium text-sm">{s.name}</p>
                              {s.description && <p className="text-xs text-muted-foreground">{s.description}</p>}
                            </div>
                          </TableCell>
                          <TableCell className="text-sm">
                            <span className="flex items-center gap-1">
                              <Clock className="w-3.5 h-3.5 text-muted-foreground" />
                              {s.duration} min
                            </span>
                          </TableCell>
                          <TableCell className="text-sm font-medium">
                            R$ {Number(s.price).toFixed(2)}
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">{s.category ?? "—"}</TableCell>
                          <TableCell>
                            {s.isActive ? (
                              <span className="flex items-center gap-1 text-green-600 text-xs">
                                <CheckCircle2 className="w-3.5 h-3.5" /> Ativo
                              </span>
                            ) : (
                              <span className="flex items-center gap-1 text-muted-foreground text-xs">
                                <XCircle className="w-3.5 h-3.5" /> Inativo
                              </span>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Profissionais */}
          <TabsContent value="professionals">
            <Card>
              <CardHeader>
                <CardTitle className="text-base font-medium">Profissionais</CardTitle>
              </CardHeader>
              <CardContent>
                {!professionals.length ? (
                  <div className="text-center py-10">
                    <Users className="w-10 h-10 text-muted-foreground mx-auto mb-2" />
                    <p className="text-muted-foreground">Nenhum profissional cadastrado.</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {professionals.map((p: any) => (
                      <div key={p.id} className="border border-border/50 rounded-xl p-4">
                        <div className="flex items-center gap-3 mb-3">
                          <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center text-primary font-medium">
                            {(p.name ?? "P").charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <p className="font-medium text-sm">{p.name}</p>
                            {p.specialty && <p className="text-xs text-muted-foreground">{p.specialty}</p>}
                          </div>
                        </div>
                        {p.phone && (
                          <p className="text-xs text-muted-foreground flex items-center gap-1">
                            <Phone className="w-3 h-3" /> {p.phone}
                          </p>
                        )}
                        {p.email && (
                          <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
                            <Mail className="w-3 h-3" /> {p.email}
                          </p>
                        )}
                        <div className="mt-2">
                          {p.isActive ? (
                            <Badge className="bg-green-100 text-green-700 text-xs">Ativo</Badge>
                          ) : (
                            <Badge variant="outline" className="text-xs">Inativo</Badge>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Equipe (Staff) */}
          <TabsContent value="staff">
            <Card>
              <CardHeader>
                <CardTitle className="text-base font-medium">Membros da Equipe</CardTitle>
              </CardHeader>
              <CardContent>
                {!staff.length ? (
                  <div className="text-center py-10">
                    <Users className="w-10 h-10 text-muted-foreground mx-auto mb-2" />
                    <p className="text-muted-foreground">Nenhum membro de equipe cadastrado.</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Membro</TableHead>
                        <TableHead>E-mail</TableHead>
                        <TableHead>Telefone</TableHead>
                        <TableHead>Nível</TableHead>
                        <TableHead>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {staff.map((s: any) => (
                        <TableRow key={s.id}>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <div className="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center text-amber-700 text-sm font-medium">
                                {(s.name ?? "M").charAt(0).toUpperCase()}
                              </div>
                              <span className="font-medium text-sm">{s.name}</span>
                            </div>
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">{s.email ?? "—"}</TableCell>
                          <TableCell className="text-sm text-muted-foreground">{s.phone ?? "—"}</TableCell>
                          <TableCell>
                            {s.role === "admin" ? (
                              <Badge className="bg-purple-100 text-purple-700 text-xs">Administrador</Badge>
                            ) : s.role === "funcionario" ? (
                              <Badge className="bg-blue-100 text-blue-700 text-xs">Funcionário</Badge>
                            ) : (
                              <Badge className="bg-green-100 text-green-700 text-xs">Caixa</Badge>
                            )}
                          </TableCell>
                          <TableCell>
                            {s.isActive ? (
                              <span className="text-green-600 text-xs flex items-center gap-1">
                                <CheckCircle2 className="w-3.5 h-3.5" /> Ativo
                              </span>
                            ) : (
                              <span className="text-muted-foreground text-xs flex items-center gap-1">
                                <XCircle className="w-3.5 h-3.5" /> Inativo
                              </span>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Agendamentos */}
          <TabsContent value="appointments">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between gap-4 flex-wrap">
                  <CardTitle className="text-base font-medium">Agendamentos</CardTitle>
                  <Select
                    value={apptStatus ?? "all"}
                    onValueChange={(v) => { setApptStatus(v === "all" ? undefined : v); setApptPage(1); }}
                  >
                    <SelectTrigger className="w-44 h-9">
                      <SelectValue placeholder="Todos os status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos os status</SelectItem>
                      <SelectItem value="pending">Pendente</SelectItem>
                      <SelectItem value="confirmed">Confirmado</SelectItem>
                      <SelectItem value="completed">Concluído</SelectItem>
                      <SelectItem value="cancelled">Cancelado</SelectItem>
                      <SelectItem value="no_show">Não Compareceu</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardHeader>
              <CardContent>
                {apptLoading ? (
                  <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
                ) : !(appointments as any)?.appointments?.length ? (
                  <div className="text-center py-10">
                    <Calendar className="w-10 h-10 text-muted-foreground mx-auto mb-2" />
                    <p className="text-muted-foreground">Nenhum agendamento encontrado.</p>
                  </div>
                ) : (
                  <>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Cliente</TableHead>
                          <TableHead>Serviço</TableHead>
                          <TableHead>Profissional</TableHead>
                          <TableHead>Data/Hora</TableHead>
                          <TableHead>Valor</TableHead>
                          <TableHead>Status</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {((appointments as any)?.appointments ?? []).map((a: any) => (
                          <TableRow key={a.id}>
                            <TableCell className="font-medium text-sm">
                              {a.clientName ?? "—"}
                              {a.isWalkIn && <Badge variant="outline" className="ml-2 text-xs">Balcão</Badge>}
                            </TableCell>
                            <TableCell className="text-sm text-muted-foreground">{a.serviceName ?? "—"}</TableCell>
                            <TableCell className="text-sm text-muted-foreground">{a.professionalName ?? "—"}</TableCell>
                            <TableCell className="text-sm">
                              {safeFormat(a.startTime ? Number(a.startTime) : null, "dd/MM/yyyy HH:mm")}
                            </TableCell>
                            <TableCell className="text-sm font-medium">
                              {a.totalPrice ? `R$ ${Number(a.totalPrice).toFixed(2)}` : "—"}
                            </TableCell>
                            <TableCell>
                              {APPT_STATUS_BADGE[a.status] ?? <Badge variant="outline">{a.status}</Badge>}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                    {(appointments as any)?.totalPages > 1 && (
                      <div className="flex items-center justify-between mt-4 pt-4 border-t">
                        <p className="text-sm text-muted-foreground">
                          Página {apptPage} de {(appointments as any).totalPages} — {(appointments as any).total} agendamentos
                        </p>
                        <div className="flex gap-2">
                          <Button variant="outline" size="sm" disabled={apptPage <= 1} onClick={() => setApptPage(p => p - 1)}>Anterior</Button>
                          <Button variant="outline" size="sm" disabled={apptPage >= (appointments as any).totalPages} onClick={() => setApptPage(p => p + 1)}>Próxima</Button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Configurações */}
          {/* Aba de Filiais */}
          <TabsContent value="branches">
            <div className="space-y-4">
              {/* Toggle hasBranches */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base font-medium flex items-center gap-2">
                    <Building2 className="w-4 h-4" />
                    Feature: Sistema de Filiais (Elite)
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium">Habilitar Filiais</p>
                      <p className="text-xs text-muted-foreground mt-0.5">Permite que o salão cadastre filiais com páginas próprias de agendamento e loja.</p>
                    </div>
                    <Button
                      variant={(salonFeatures as any)?.hasBranches ? "default" : "outline"}
                      size="sm"
                      onClick={() => salonId && updateFeaturesMutation.mutate({
                        salonId,
                        hasBranches: !(salonFeatures as any)?.hasBranches,
                      })}
                      disabled={updateFeaturesMutation.isPending}
                    >
                      {updateFeaturesMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : (salonFeatures as any)?.hasBranches ? "Ativo" : "Inativo"}
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Lista de filiais */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base font-medium">Filiais Cadastradas</CardTitle>
                </CardHeader>
                <CardContent>
                  {branchesLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                    </div>
                  ) : !branches || (branches as any[]).length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <Building2 className="w-8 h-8 mx-auto mb-2 opacity-30" />
                      <p className="text-sm">Nenhuma filial cadastrada.</p>
                    </div>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Nome</TableHead>
                          <TableHead>Slug</TableHead>
                          <TableHead>Endereço</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Criada em</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {(branches as any[]).map((branch) => (
                          <TableRow key={branch.id}>
                            <TableCell className="font-medium">{branch.name}</TableCell>
                            <TableCell className="font-mono text-xs">{branch.slug}</TableCell>
                            <TableCell className="text-sm text-muted-foreground">{branch.address ?? "—"}</TableCell>
                            <TableCell>
                              <Badge variant={branch.status === "active" ? "default" : "secondary"}>
                                {branch.status === "active" ? "Ativa" : "Inativa"}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-sm text-muted-foreground">
                              {safeFormat(branch.createdAt, "dd/MM/yyyy")}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Agente Zaia */}
          <TabsContent value="zaia">
            <div className="space-y-4">
              {/* Status atual */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base font-medium flex items-center gap-2">
                    <Bot className="w-4 h-4" />
                    Agente Zaia Vinculado
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {salon.zaiaAgentId ? (
                    <div className="space-y-4">
                      <div className="flex items-center gap-3 p-3 bg-green-50 dark:bg-green-950/30 rounded-lg border border-green-200 dark:border-green-800">
                        <CheckCircle2 className="w-5 h-5 text-green-600 shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-green-800 dark:text-green-200">Agente vinculado</p>
                          <p className="text-xs text-green-600 dark:text-green-400 font-mono mt-0.5">
                            ID: {salon.zaiaAgentId}
                          </p>
                        </div>
                        <a
                          href="https://app.zaia.app"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-green-700 dark:text-green-300 underline underline-offset-2 hover:no-underline shrink-0"
                        >
                          Ver no painel Zaia ↗
                        </a>
                      </div>

                      {/* Atualizar behavior */}
                      <div className="flex items-center justify-between gap-4">
                        <div>
                          <p className="text-sm font-medium">Atualizar Behavior</p>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            Reenvia as instruções e URLs das APIs do salão para o agente.
                          </p>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          className="gap-2 shrink-0"
                          disabled={linkZaiaMutation.isPending}
                          onClick={() => {
                            if (!salonId) return;
                            linkZaiaMutation.mutate({
                              salonId,
                              zaiaAgentId: salon.zaiaAgentId!,
                              updateBehavior: true,
                            });
                          }}
                        >
                          {linkZaiaMutation.isPending
                            ? <Loader2 className="w-4 h-4 animate-spin" />
                            : <RefreshCw className="w-4 h-4" />}
                          Atualizar Behavior
                        </Button>
                      </div>

                      {/* Desvincular */}
                      <div className="border-t border-border/50 pt-4">
                        <p className="text-sm font-medium text-destructive mb-3">Zona de Perigo</p>
                        <div className="flex flex-wrap gap-3">
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button variant="outline" size="sm" className="gap-2 text-orange-600 border-orange-300 hover:bg-orange-50">
                                <Link2Off className="w-4 h-4" />
                                Desvincular (manter na Zaia)
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Desvincular agente?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  O agente Zaia #<strong>{salon.zaiaAgentId}</strong> será desvinculado deste salão,
                                  mas continuará existindo no painel da Zaia. Você pode vinculá-lo novamente a qualquer momento.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                <AlertDialogAction
                                  className="bg-orange-600 hover:bg-orange-700"
                                  onClick={() => salonId && unlinkZaiaMutation.mutate({ salonId, deleteFromZaia: false })}
                                >
                                  Desvincular
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>

                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button variant="outline" size="sm" className="gap-2 text-destructive border-destructive/30 hover:bg-destructive/10">
                                <XCircle className="w-4 h-4" />
                                Desvincular e deletar da Zaia
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Deletar agente da Zaia?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  O agente Zaia #<strong>{salon.zaiaAgentId}</strong> será <strong>permanentemente deletado</strong> do painel
                                  da Zaia e desvinculado deste salão. Esta ação não pode ser desfeita.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                <AlertDialogAction
                                  className="bg-destructive hover:bg-destructive/90"
                                  onClick={() => salonId && unlinkZaiaMutation.mutate({ salonId, deleteFromZaia: true })}
                                >
                                  Deletar e Desvincular
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center gap-3 p-3 bg-muted/40 rounded-lg border border-border/50">
                      <AlertCircle className="w-5 h-5 text-muted-foreground shrink-0" />
                      <div>
                        <p className="text-sm font-medium">Nenhum agente vinculado</p>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          Vincule um agente existente abaixo ou crie um novo pelo fluxo automático no painel do salão.
                        </p>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Vincular agente manualmente */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base font-medium flex items-center gap-2">
                    <Link2 className="w-4 h-4" />
                    Vincular Agente Existente
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground mb-4">
                    Use esta opção quando o agente já foi criado diretamente no painel da Zaia e você quer associá-lo a este salão.
                    O ID do agente pode ser encontrado na URL do painel Zaia: <code className="text-xs bg-muted px-1 py-0.5 rounded">app.zaia.app/agents/<strong>[ID]</strong></code>.
                  </p>

                  <div className="space-y-4">
                    <div className="space-y-1.5">
                      <Label htmlFor="zaiaAgentId">ID do Agente Zaia</Label>
                      <Input
                        id="zaiaAgentId"
                        type="number"
                        min={1}
                        placeholder="Ex: 12345"
                        value={zaiaAgentIdInput}
                        onChange={(e) => setZaiaAgentIdInput(e.target.value)}
                        className="max-w-xs"
                      />
                    </div>

                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id="updateBehavior"
                        checked={updateBehavior}
                        onChange={(e) => setUpdateBehavior(e.target.checked)}
                        className="rounded border-border"
                      />
                      <Label htmlFor="updateBehavior" className="cursor-pointer font-normal text-sm">
                        Atualizar behavior do agente com dados deste salão (recomendado)
                      </Label>
                    </div>

                    <Button
                      onClick={() => {
                        const id = parseInt(zaiaAgentIdInput, 10);
                        if (!salonId || isNaN(id) || id <= 0) {
                          toast.error("ID inválido", { description: "Informe um ID numérico positivo." });
                          return;
                        }
                        linkZaiaMutation.mutate({ salonId, zaiaAgentId: id, updateBehavior });
                      }}
                      disabled={linkZaiaMutation.isPending || !zaiaAgentIdInput}
                      className="gap-2"
                    >
                      {linkZaiaMutation.isPending
                        ? <Loader2 className="w-4 h-4 animate-spin" />
                        : <Link2 className="w-4 h-4" />}
                      Vincular Agente
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Agente IA Nativo */}
          <TabsContent value="native-agent">
            <NativeAgentTab salonId={salonId} />
          </TabsContent>

          <TabsContent value="config">
            <Card>
              <CardHeader>
                <CardTitle className="text-base font-medium">Configurações do Salão</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <h3 className="text-sm font-semibold text-foreground uppercase tracking-wide">Informações Gerais</h3>
                    <div className="space-y-3">
                      {[
                        { label: "Nome", value: salon.name },
                        { label: "Telefone", value: salon.phone },
                        { label: "E-mail", value: salon.email },
                        { label: "Endereço", value: salon.address },
                        { label: "Cidade", value: salon.city },
                        { label: "Slug (URL)", value: salon.slug },
                      ].map(({ label, value }) => (
                        <div key={label} className="flex items-start justify-between gap-4 py-2 border-b border-border/30">
                          <span className="text-sm text-muted-foreground">{label}</span>
                          <span className="text-sm font-medium text-foreground text-right">{value ?? "—"}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="space-y-4">
                    <h3 className="text-sm font-semibold text-foreground uppercase tracking-wide">Plano e Assinatura</h3>
                    <div className="space-y-3">
                      {[
                        { label: "Status", value: salon.status },
                        { label: "Plano ID", value: salon.planId },
                        { label: "Trial até", value: safeFormat(salon.trialEndsAt, "dd/MM/yyyy") },
                        { label: "Cadastro", value: safeFormat(salon.createdAt, "dd/MM/yyyy HH:mm") },
                        { label: "Fuso Horário", value: salon.timezone },
                        { label: "Moeda", value: salon.currency },
                      ].map(({ label, value }) => (
                        <div key={label} className="flex items-start justify-between gap-4 py-2 border-b border-border/30">
                          <span className="text-sm text-muted-foreground">{label}</span>
                          <span className="text-sm font-medium text-foreground text-right">{value ?? "—"}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
